import React, { useState } from 'react';
import { Plus, Trash2, ChevronUp, ChevronDown, LayoutTemplate } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { resolveAssessmentType, renumberMilestones } from '@/lib/offeringMigration';
import type { CurriculumMilestone, AssessmentType } from '@/types/offering';

interface Props {
  milestones: CurriculumMilestone[];
  onChange: (milestones: CurriculumMilestone[]) => void;
  disabled?: boolean;
  hint?: React.ReactNode;
}

// ── Assessment options ────────────────────────────────────────────────────────

const ASSESSMENT_OPTIONS: { value: AssessmentType; label: string }[] = [
  { value: 'NONE',       label: 'No assessment' },
  { value: 'QUIZ',       label: 'Quiz' },
  { value: 'ASSIGNMENT', label: 'Assignment' },
  { value: 'MOCK_TEST',  label: 'Mock Test' },
  { value: 'PROJECT',    label: 'Project' },
];

const ASSESSMENT_BADGE: Record<AssessmentType, string> = {
  NONE:       '',
  QUIZ:       'Quiz',
  ASSIGNMENT: 'Assignment',
  MOCK_TEST:  'Mock Test',
  PROJECT:    'Project',
};

// ── Templates ─────────────────────────────────────────────────────────────────

type Template = { label: string; weeks: Omit<CurriculumMilestone, 'weekNumber'>[] };

const TEMPLATES: Template[] = [
  {
    label: '4-Week Intensive',
    weeks: [
      { title: 'Foundations & Core Concepts', description: 'Build the fundamentals needed for the rest of the course.', topicIds: [], assessmentType: 'QUIZ' },
      { title: 'Deep Dive & Practice',         description: 'Work through problems and reinforce understanding.',        topicIds: [], assessmentType: 'ASSIGNMENT' },
      { title: 'Application & Case Studies',   description: 'Apply concepts to real-world scenarios.',                   topicIds: [], assessmentType: 'NONE' },
      { title: 'Revision & Final Assessment',  description: 'Consolidate learning and test readiness.',                  topicIds: [], assessmentType: 'MOCK_TEST' },
    ],
  },
  {
    label: '8-Week Course',
    weeks: [
      { title: 'Introduction & Orientation',  description: 'Overview of the course and setting expectations.',           topicIds: [], assessmentType: 'NONE' },
      { title: 'Core Module 1',               description: 'First major topic block.',                                   topicIds: [], assessmentType: 'QUIZ' },
      { title: 'Core Module 2',               description: 'Second major topic block.',                                  topicIds: [], assessmentType: 'ASSIGNMENT' },
      { title: 'Mid-Course Review',           description: 'Recap and address gaps before moving forward.',              topicIds: [], assessmentType: 'MOCK_TEST' },
      { title: 'Advanced Module 1',           description: 'First advanced topic block.',                                topicIds: [], assessmentType: 'NONE' },
      { title: 'Advanced Module 2',           description: 'Second advanced topic block.',                               topicIds: [], assessmentType: 'QUIZ' },
      { title: 'Practice & Problem Solving',  description: 'Intensive practice with complex problems.',                  topicIds: [], assessmentType: 'ASSIGNMENT' },
      { title: 'Final Revision & Assessment', description: 'Full course revision and final test.',                       topicIds: [], assessmentType: 'MOCK_TEST' },
    ],
  },
  {
    label: '12-Week Program',
    weeks: [
      { title: 'Introduction',               description: 'Course overview, tools, and expectations.',         topicIds: [], assessmentType: 'NONE' },
      { title: 'Module 1 — Basics',          description: 'First foundational block.',                         topicIds: [], assessmentType: 'QUIZ' },
      { title: 'Module 2 — Core A',          description: 'Core concepts part A.',                             topicIds: [], assessmentType: 'ASSIGNMENT' },
      { title: 'Module 3 — Core B',          description: 'Core concepts part B.',                             topicIds: [], assessmentType: 'NONE' },
      { title: 'Mid-Term Review',            description: 'Recap of modules 1–4 and gap analysis.',            topicIds: [], assessmentType: 'MOCK_TEST' },
      { title: 'Module 4 — Intermediate A',  description: 'Intermediate concepts part A.',                     topicIds: [], assessmentType: 'QUIZ' },
      { title: 'Module 5 — Intermediate B',  description: 'Intermediate concepts part B.',                     topicIds: [], assessmentType: 'ASSIGNMENT' },
      { title: 'Module 6 — Advanced',        description: 'Advanced topics and techniques.',                   topicIds: [], assessmentType: 'NONE' },
      { title: 'Applied Practice A',         description: 'Real-world application exercises.',                  topicIds: [], assessmentType: 'QUIZ' },
      { title: 'Applied Practice B',         description: 'Advanced problem-solving scenarios.',                topicIds: [], assessmentType: 'ASSIGNMENT' },
      { title: 'Full Revision',              description: 'Comprehensive review of all modules.',               topicIds: [], assessmentType: 'MOCK_TEST' },
      { title: 'Final Assessment',           description: 'End-of-program test and feedback session.',         topicIds: [], assessmentType: 'MOCK_TEST' },
    ],
  },
];

// ── Component ─────────────────────────────────────────────────────────────────

export function MilestonesBuilder({ milestones, onChange, disabled, hint }: Props) {
  const [showTemplates, setShowTemplates] = useState(false);

  const add = () => {
    onChange(renumberMilestones([...milestones, { weekNumber: 0, title: '', description: '', topicIds: [], assessmentType: 'NONE' }]));
  };

  const remove = (i: number) => onChange(renumberMilestones(milestones.filter((_, idx) => idx !== i)));

  const move = (i: number, dir: -1 | 1) => {
    const next = [...milestones];
    const swap = i + dir;
    if (swap < 0 || swap >= next.length) return;
    [next[i], next[swap]] = [next[swap], next[i]];
    onChange(renumberMilestones(next));
  };

  const update = <K extends keyof CurriculumMilestone>(i: number, field: K, value: CurriculumMilestone[K]) => {
    onChange(milestones.map((m, idx) => idx === i ? { ...m, [field]: value } : m));
  };

  const applyTemplate = (tpl: Template) => {
    onChange(renumberMilestones(tpl.weeks.map((w, i) => ({ weekNumber: i + 1, topicIds: [], ...w }))));
    setShowTemplates(false);
  };

  return (
    <div className="space-y-3">
      {/* ── Header row ── */}
      <div className="flex items-center justify-between gap-2">
        <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1">
          Curriculum Milestones{hint}
        </Label>
        {!disabled && (
          <button
            type="button"
            onClick={() => setShowTemplates(v => !v)}
            className="flex items-center gap-1.5 text-xs font-medium text-indigo-600 hover:text-indigo-700 transition-colors"
          >
            <LayoutTemplate className="w-3.5 h-3.5" />
            Use a template
          </button>
        )}
      </div>

      {/* ── Template picker ── */}
      {showTemplates && (
        <div className="rounded-xl border border-indigo-100 bg-indigo-50/60 p-3 space-y-2">
          <p className="text-xs text-slate-500 mb-2">Choose a starting point — you can edit everything after.</p>
          <div className="flex flex-wrap gap-2">
            {TEMPLATES.map(tpl => (
              <button
                key={tpl.label}
                type="button"
                onClick={() => applyTemplate(tpl)}
                className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-white border border-indigo-200 text-indigo-700 hover:bg-indigo-50 active:scale-95 transition-all"
              >
                {tpl.label}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setShowTemplates(false)}
              className="text-xs px-3 py-1.5 rounded-lg text-slate-400 hover:text-slate-600 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* ── Empty state ── */}
      {milestones.length === 0 && (
        <p className="text-sm text-muted-foreground italic">No milestones added yet. Add one or pick a template above.</p>
      )}

      {/* ── Milestone rows ── */}
      {milestones.map((m, i) => {
        const assessmentType = resolveAssessmentType(m);
        return (
          <div key={i} className="rounded-xl border border-border bg-muted/20 overflow-hidden">
            {/* Week badge + reorder + delete */}
            <div className="flex items-center gap-2 px-3 pt-3 pb-2">
              <span className="text-xs font-extrabold px-2.5 py-1 rounded-lg bg-indigo-100 text-indigo-700 shrink-0">
                Week {m.weekNumber}
              </span>
              <div className="flex-1" />
              {!disabled && (
                <>
                  <button type="button" onClick={() => move(i, -1)} disabled={i === 0}
                    className={cn("p-1 rounded text-muted-foreground hover:text-foreground transition-colors", i === 0 && "opacity-30 cursor-not-allowed")}>
                    <ChevronUp className="w-3.5 h-3.5" />
                  </button>
                  <button type="button" onClick={() => move(i, 1)} disabled={i === milestones.length - 1}
                    className={cn("p-1 rounded text-muted-foreground hover:text-foreground transition-colors", i === milestones.length - 1 && "opacity-30 cursor-not-allowed")}>
                    <ChevronDown className="w-3.5 h-3.5" />
                  </button>
                  <button type="button" onClick={() => remove(i)}
                    className="p-1 rounded text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </>
              )}
            </div>

            <div className="px-3 pb-3 space-y-2">
              {/* Title */}
              <div>
                <Label className="text-xs text-muted-foreground">Title</Label>
                <Input
                  value={m.title ?? ''}
                  onChange={e => update(i, 'title', e.target.value)}
                  disabled={disabled}
                  placeholder="e.g. Quadratic Equations"
                  className={`h-8 text-sm mt-0.5 ${(m.title?.length ?? 0) > 200 ? 'border-red-500' : ''}`}
                  maxLength={200}
                />
                {(m.title?.length ?? 0) > 160 && (
                  <p className={`text-xs mt-0.5 ${(m.title?.length ?? 0) > 200 ? 'text-red-500' : 'text-muted-foreground'}`}>
                    {m.title.length}/200
                  </p>
                )}
              </div>

              {/* Description */}
              <div>
                <Label className="text-xs text-muted-foreground">Description <span className="text-muted-foreground/60 font-normal">(optional)</span></Label>
                <Input
                  value={m.description ?? ''}
                  onChange={e => update(i, 'description', e.target.value)}
                  disabled={disabled}
                  placeholder="What will students learn or do this week?"
                  className="h-8 text-sm mt-0.5"
                  maxLength={300}
                />
              </div>

              {/* Assessment type */}
              <div>
                <Label className="text-xs text-muted-foreground">Assessment</Label>
                <select
                  value={assessmentType}
                  onChange={e => update(i, 'assessmentType', e.target.value as AssessmentType)}
                  disabled={disabled}
                  className="mt-0.5 h-8 w-full rounded-md border border-input bg-background px-2.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  {ASSESSMENT_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        );
      })}

      {/* ── Add button ── */}
      {!disabled && (
        <Button type="button" variant="outline" size="sm" className="gap-1.5 cursor-pointer" onClick={add}>
          <Plus className="w-3.5 h-3.5" /> Add Milestone
        </Button>
      )}
    </div>
  );
}

export { ASSESSMENT_BADGE };
